import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  isIngestionAction,
  requiredParametersOf,
  type ActionRecord,
} from "@/shared/domain/actions";
import { useAction, useActions } from "@/shared/domain/actions/hooks";
import { type TriggerTemplateCatalog } from "@/shared/domain/triggers";
import { Combobox, Field, FormBlock } from "@/shared/foundation/primitives";

import { type TriggeringSubject } from "./inputSourceCopy";
import styles from "./invocationSpec.module.css";
import { InvocationSpecFields } from "./InvocationSpecFields";

/** The action an invocation block names, as the form holds it. */
type ActionReferenceValues = {
  name: string;
  owner: string | null;
  digest: string | null;
} | null;

/** The actions offered from one place, under the name of that place. */
interface ActionGroup {
  source: "ingestion" | "org" | "actionhub";
  label: string;
  items: readonly ActionRecord[];
}

export interface ActionInvocationEditorProps {
  /**
   * Where the invocation block lives in the enclosing form: `"targets.2.spec"`
   * on a trigger target, `"invocation"` on an ingestion rule.
   */
  name: string;
  /**
   * Where the chosen action's required parameter names are written, so the
   * enclosing form's schema can insist on them.
   */
  requiredParametersName: string;
  /**
   * The org that owns the action when the stored reference has no owner: the
   * org of the trigger or rule the invocation belongs to. Undefined only while
   * there is no current org.
   */
  defaultActionOwner: string | undefined;
  /** The hint under the "Action" heading. */
  actionHint: string;
  /**
   * The `{{ }}` paths parameter values may reference. Without one, parameters
   * take fixed values only.
   */
  templateCatalog?: TriggerTemplateCatalog;
  /** Rendered at the top of "Input data": what the invocation is already handed. */
  inputDataHeader?: React.ReactNode;
  /** Whether the invocation runs on a dataset its output can go back to. */
  hasSourceDataset: boolean;
  /**
   * What the firing is about, when its namespace carries a dataset that
   * "Input data" may offer.
   */
  triggeringSubject?: TriggeringSubject;
  /** A file name the invocation might run on, for an action form's previews. */
  exampleFileName?: string;
  /**
   * Every run starts on a file just uploaded, as on an ingestion rule, so an
   * action form leaves out settings about files already ingested.
   */
  startsOnUpload?: boolean;
  /**
   * Lead the picker with an "Ingestion actions" group, from both places, ahead
   * of the rest: what an ingestion rule is nearly always for.
   */
  ingestionActionsFirst?: boolean;
  /** Shown under the picker, given the chosen action: a nudge or a warning. */
  renderActionNotice?: (action: ActionRecord | null) => React.ReactNode;
  "data-testid"?: string;
}

/**
 * The action to invoke, and everything about how to run it: the picker over
 * the org's and the Action Hub's actions, then the invocation block for the
 * one chosen.
 *
 * Knows nothing of what surrounds it; the caller says where the values live
 * and what the invocation is already handed.
 */
export function ActionInvocationEditor({
  name,
  requiredParametersName,
  defaultActionOwner,
  actionHint,
  templateCatalog,
  inputDataHeader,
  hasSourceDataset,
  triggeringSubject,
  exampleFileName,
  startsOnUpload = false,
  ingestionActionsFirst = false,
  renderActionNotice,
  "data-testid": testId,
}: ActionInvocationEditorProps) {
  const { control, setValue, getValues } = useFormContext();
  const actionTitleId = React.useId();
  const orgActions = useActions("org");
  const hubActions = useActions("actionhub");

  const reference = useWatch({
    control,
    name: `${name}.action`,
  }) as ActionReferenceValues;

  // The org's own actions lead, since those are the ones it wrote for itself.
  const groups: readonly ActionGroup[] = actionGroups(
    orgActions.data ?? [],
    hubActions.data ?? [],
    ingestionActionsFirst,
  );
  const offered = groups.flatMap((group) => group.items);
  // The stored action may be one this org cannot list (a hub action pinned by
  // digest), so it is fetched by name rather than found in the offered list.
  const storedAction = useAction(
    reference?.name,
    reference?.digest ?? undefined,
    { resourceOwnerId: reference?.owner ?? defaultActionOwner },
  );
  const chosen =
    offered.find(
      (action) =>
        action.name === reference?.name &&
        (reference.owner === null || action.org_id === reference.owner),
    ) ??
    storedAction.data ??
    null;
  // Note the parameters the action cannot run without, once it is known --
  // just picked, or loaded for a stored spec -- so validation can insist on
  // them. Written only on a change, so loading does not dirty the form.
  React.useEffect(() => {
    const next = requiredParametersOf(chosen);
    const current = (getValues(requiredParametersName) ?? []) as string[];
    if (current.join("\u0000") !== next.join("\u0000")) {
      setValue(requiredParametersName, next);
    }
  }, [chosen, requiredParametersName, getValues, setValue]);

  return (
    <div className={styles.editor} data-testid={testId}>
      <FormBlock titleId={actionTitleId} title="Action" hint={actionHint}>
        <Controller
          control={control}
          name={`${name}.action`}
          render={({ field, fieldState }) => (
            <Field.Root invalid={fieldState.invalid}>
              <Combobox<ActionRecord>
                items={groups}
                value={chosen}
                onValueChange={(action) => {
                  field.onChange(
                    action === null
                      ? null
                      : {
                          name: action.name,
                          owner: action.org_id,
                          // A newly chosen action invokes its latest version; a
                          // stored digest pins the one it replaced.
                          digest: null,
                        },
                  );
                  // Which pairs of vCPU and memory are allowed is the action's
                  // own business, so a pair chosen for the last action is
                  // cleared and the new action's own applies.
                  setValue(`${name}.overrides.cpu`, undefined, {
                    shouldDirty: true,
                  });
                  setValue(`${name}.overrides.memory`, undefined, {
                    shouldDirty: true,
                  });
                }}
                itemToStringLabel={(action) => action.name}
                isItemEqualToValue={(item, value) =>
                  item.name === value.name && item.org_id === value.org_id
                }
                autoHighlight
                size="lg"
              >
                <Combobox.TriggerInput
                  placeholder="Search actions"
                  aria-labelledby={actionTitleId}
                  ref={field.ref}
                />
                <Combobox.Content>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(group: ActionGroup) => (
                      <Combobox.Group key={group.source} items={group.items}>
                        <Combobox.GroupLabel>{group.label}</Combobox.GroupLabel>
                        <Combobox.Collection>
                          {(action: ActionRecord) => (
                            <Combobox.Item
                              key={`${action.org_id}/${action.name}`}
                              value={action}
                            />
                          )}
                        </Combobox.Collection>
                      </Combobox.Group>
                    )}
                  </Combobox.List>
                </Combobox.Content>
              </Combobox>
              <Field.Error match>{fieldState.error?.message}</Field.Error>
            </Field.Root>
          )}
        />
        {renderActionNotice?.(chosen)}
      </FormBlock>

      {chosen !== null && (
        <InvocationSpecFields
          name={name}
          action={chosen}
          templateCatalog={templateCatalog}
          inputDataHeader={inputDataHeader}
          hasSourceDataset={hasSourceDataset}
          triggeringSubject={triggeringSubject}
          exampleFileName={exampleFileName}
          startsOnUpload={startsOnUpload}
        />
      )}
    </div>
  );
}

function actionGroups(
  org: readonly ActionRecord[],
  hub: readonly ActionRecord[],
  ingestionFirst: boolean,
): ActionGroup[] {
  if (!ingestionFirst) {
    return [
      { source: "org", label: "Your actions", items: org },
      { source: "actionhub", label: "Action Hub", items: hub },
    ];
  }
  const others = (actions: readonly ActionRecord[]) =>
    actions.filter((action) => !isIngestionAction(action));
  const groups: ActionGroup[] = [
    {
      source: "ingestion",
      label: "Ingestion actions",
      // Deduplicated: an action the org can list from both places is offered once.
      items: [
        ...new Map(
          [...org, ...hub]
            .filter(isIngestionAction)
            .map((action) => [`${action.org_id}/${action.name}`, action]),
        ).values(),
      ],
    },
    { source: "org", label: "Your actions", items: others(org) },
    { source: "actionhub", label: "Action Hub", items: others(hub) },
  ];
  return groups.filter((group) => group.items.length > 0);
}
